import { useEffect } from 'react';

/**
 * Challenges a browser-level exit while a form holds unsaved work.
 *
 * This covers closing the tab, reloading, and typing a new URL — the exits the
 * browser owns, where the confirmation is the browser's own dialog and its
 * wording cannot be chosen. Modern browsers ignore any custom message; the
 * only thing that matters is that `preventDefault` is called.
 *
 * It deliberately does NOT cover in-app navigation. The app mounts a plain
 * `BrowserRouter`, and `useBlocker` needs a data router — attempting to
 * intercept history any other way produces a guard that fires inconsistently
 * and, worse, sometimes leaves the URL changed while the component stays
 * mounted. In-app exits are guarded where they are triggered, by asking before
 * navigating, which also lets the question be phrased in the app's own words.
 *
 * The real safety net for everything else is the draft: a form that writes
 * what was typed does not depend on catching the exit at all.
 */
export function useUnsavedGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers keyed off the return value rather than
      // preventDefault. Harmless where it is ignored.
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}
